import React, { Suspense, lazy } from "react";

const MyCalendarPage = lazy(() => import('pages/my/MyCalendarPage'));
const MyInboxPage    = lazy(() => import('pages/my/MyInboxPage'));
const LogisticDashboardPage = lazy(() => import('pages/crm/dashboard/LogisticDashboardPage'));
import { Navigate, Route, Routes } from "react-router-dom";
import AuthSimpleLayout from "../layouts/AuthSimpleLayout";
import MainLayout from "../layouts/MainLayout";
import PublicLayout from "../layouts/PublicLayout";
import ChangelogPage from "pages/documentation/ChangelogPage";
import UserGuidePage from "pages/documentation/UserGuidePage";
import Error404 from "components/layouts/errors/Error404";
import Error500 from "components/layouts/errors/Error500";

import LoginStitch from "components/layouts/authentication/AuthStitch/LoginStitch";
import LoginOverseaStitch from "components/layouts/authentication/AuthStitch/LoginOverseaStitch";

import SimpleForgetPassword from "components/layouts/authentication/simple/ForgetPassword";
import SimplePasswordReset from "components/layouts/authentication/simple/PasswordReset";
import SimpleConfirmMail from "components/layouts/authentication/simple/ConfirmMail";
import PrivateRoute from "./privateRoute";
import PIFPage from 'pages/crm/reports/PIFPage';
import WarrantyPage from 'pages/crm/warranty/WarrantyPage';
import PriceMatrixPage from 'pages/crm/price-matrix/PriceMatrixPage';
import InteractionPage from 'pages/crm/project/InteractionPage';
import MasterProjectSchedulePage from 'pages/crm/project/MasterProjectSchedulePage';
import MasterSummaryPage from 'pages/crm/reports/MasterSummaryPage';
import ICCCPage from 'pages/crm/reports/ICCCPage';
import QuotationPage from 'pages/crm/project/QuotationPage';
import ProjectScheduleOEPage from 'pages/crm/reports/ProjectScheduleOEPage';
import ProjectSchedulePMDPage from 'pages/crm/project/ProjectSchedulePMDPage';
import EstimateAssignmentSchedulePage from 'pages/crm/reports/EstimateAssignmentSchedule';
import EngineeringJobProgressPage from 'pages/crm/reports/EngineeringJobProgressPage';
import ShopByChangeOrderPage from 'pages/crm/reports/ShopByChangeOrderPage';
import ShopSummaryPage from 'pages/crm/reports/ShopSummaryPage';
import Contracts from "components/app/crm/contract/report/ContractReportPage";
import DraftCheckPage from "pages/crm/draft-check/DraftCheckPage";
import PifCheckPage from "pages/crm/pif-check/PifCheckPage";
import ComparisonViewerPage from "pages/crm/draft-check/ComparisonViewerPage";
import SalesUnits from "components/app/crm/settings/sales_unit/SalesUnitListPage";
import MeetingRoomSystem from "components/app/bookings/meeting-room/MeetingRoomSystem";
import HrmHubSystem from "components/app/hrm-hub/HrmHubSystem";
import TravelBookingPage from "pages/bookings/travel/TravelBookingPage";
import TravelBookingDetailPage from "pages/bookings/travel/TravelBookingDetailPage";
import TravelBookingFormPage from "pages/bookings/travel/TravelBookingFormPage";
import ExpensePolicyPage from "pages/bookings/travel/ExpensePolicyPage";
import ApprovalFlowPage from "pages/bookings/travel/ApprovalFlowPage";

import PermissionPage from "pages/authorization/PermissionListPage";
import FeatureFlagsPage from "pages/authorization/FeatureFlagsPage";
import DocumentExtractionsPage from "pages/authorization/DocumentExtractionsPage";
import RolePage from "pages/authorization/RolePage";
import RoleDetailPage from "pages/authorization/role/RoleDetailPage";
import UserPage from "pages/authorization/UserPage";
import UserDetailPage from "pages/authorization/user/UserDetailPage";
import RoleUserPermissionPage from "pages/authorization/RoleUserPermissionReportPage";

import SalesUnitDetail from "components/app/crm/settings/sales_unit/detail/SalesUnitDetail";

import Employees from "components/app/organization/EmployeeListPage";
import EmployeeDetail from "components/app/organization/employee/detail/EmployeeDetail";
import NewEmployee from "components/app/organization/employee/NewEmployee";
import EmployeeOrgChart from "components/app/organization/employee/org-chart/EmployeeOrgChartPage";
import EditEmployee from "components/app/organization/employee/EditEmployee";
import Departments from "components/app/organization/DepartmentListPage";
import Sites from "components/app/organization/SiteListPage";
import NewContact from "components/app/crm/contact/NewContact";
import EditContact from "components/app/crm/contact/EditContact";
import ContactDetail from "components/app/crm/contact/detail/ContactDetail";
import NotificationList from "components/common/notification/NotificationList";
import NewCustomer from "components/app/crm/customer/NewCustomer";
import EditCustomer from "components/app/crm/customer/EditCustomer";
import CustomerDetail from "components/app/crm/customer/detail/CustomerDetail";
import Cars from "pages/bookings/car/CarSettingsPage";
import Drivers from "pages/bookings/car/DriverSettingsPage";
import TemplatePage from "pages/crm/template/TemplatePage";
import CarBookingPage from "pages/bookings/car/CarBookingPage";
import CarBookingFormPage from "pages/bookings/car/CarBookingFormPage";
import CarBookingDetail from "pages/bookings/car/CarBookingDetail";
import TransactionDriverConfirm from "components/app/bookings/car/transaction/TransactionDriverConfirm";
import CarCalendar from "components/app/bookings/car/CarCalendar";
import TransactionDriverUpdateDeparture from "components/app/bookings/car/transaction/TransactionDriverUpdateDeparture";
import VerifyEmail from "components/layouts/authentication/VerifyEmail";
import Error403 from "components/layouts/errors/Error403";
import PrintBooking from "components/app/bookings/travel/booking/print/PrintBooking";
import NewWaiver from "components/app/crm/waiver/NewWaiver";
import EditWaiver from "components/app/crm/waiver/EditWaiver";
import NewChangeOrder from "components/app/crm/change-order/NewChangeOrder";
import EditChangeOrder from "components/app/crm/change-order/EditChangeOrder";
import ChangeOrderPrint from "components/app/crm/change-order/ChangeOrderPrint";
import NewPIF from "components/app/crm/pif/NewPIF";
import EditPIF from "components/app/crm/pif/EditPIF";
import PIFDetail from "components/app/crm/pif/PIFDetail";
import PIFPrint from "components/app/crm/pif/PIFPrint";
import ChangeOrderDetail from "components/app/crm/change-order/ChangeOrderDetail";
import Waivers from "pages/crm/waiver/WaiverListPage";
import WaiverDetail from "components/app/crm/waiver/WaiverDetail";
import WaiverPrint from "components/app/crm/waiver/print/WaiverPrint";

import QuotationDashboardPage from "pages/crm/dashboard/QuotationDashboardPage";
import ChangeOrderDashboardPage from "pages/crm/dashboard/ChangeOrderDashboardPage";
import ContractDashboardPage from "pages/crm/dashboard/ContractDashboardPage";
import SalesDashboardPage from "pages/crm/dashboard/SalesDashboardPage";
import WaiverDashboardPage from "pages/crm/dashboard/WaiverDashboardPage";
import JobEntryDashboardPage from "pages/crm/dashboard/JobEntryDashboardPage";
import CrmOverviewDashboardPage from "pages/crm/dashboard/CrmOverviewDashboardPage";
import CarBookingDashboardPage from "pages/bookings/dashboard/CarBookingDashboardPage";
import UserSessionsDashboardPage from "pages/authorization/UserSessionsDashboardPage";
import AuditLogsPage from "pages/authorization/AuditLogsPage";
import TravelBookingDashboardPage from "pages/bookings/dashboard/TravelBookingDashboardPage";
import {
  PaymentRequestDetail,
  PaymentRequestPrint,
  PaymentRequestForm
} from "components/app/erp/payment-request";
import { PurchaseRequestDetail } from "components/app/erp/purchase-request";
import { PurchaseOrderDetail } from "components/app/erp/purchase-order";
import PaymentRequestPage from "pages/erp/PaymentRequestPage";
import PurchaseRequisitionsPage from "pages/erp/PurchaseRequisitionsPage";
import PurchaseOrderPage from "pages/erp/PurchaseOrderPage";

import Leads from "components/app/crm/lead/Leads";
import LeadDetail from "components/app/crm/lead/LeadDetail";
import NewLead from "components/app/crm/lead/NewLead";
import EditLead from "components/app/crm/lead/EditLead";
import BackgroundJobPage from "pages/background-job/BackgroundJobPage";
import MyWorkSpacePage from "pages/workspace/MyWorkSpacePage";
import FaqPage from "pages/documentation/FaqPage";
import ProfileUser from "pages/ProfilePage";
import AppSettingsPage from "pages/settings/AppSettingsPage";


import IntegrationApplication from "components/app/integration/Applications";
import ApplicationDetail from "components/app/integration/ApplicationDetail";
import NewApplication from "components/app/integration/NewApplication";
import EditApplication from "components/app/integration/EditApplication";
import { DriveListPage } from "pages/drive";
import DriveDocViewer from "components/app/drive/DriveDocViewer";
import { DriveTrashPage } from "pages/drive";
import DriveOnlyOfficeEditor from "components/app/drive/DriveOnlyOfficeEditor";
import DocSearch from "components/app/drive/DocSearch";
import DocChatBot from "components/app/ai/DocChatBot";

import RevisionPIFEdit from 'components/app/crm/change-order-revision/RevisionPIFEdit';
import CRMContacts from "components/app/crm/contact/ContactListPage";
import Customers from "components/app/crm/customer/CustomerListPage";
import CustomerChecking from "components/app/crm/customer/CustomerChecking";
import PositionGroups from "components/app/organization/PositionGroups";
import ProtectedRoute from "./ProtectedRoute";
import PrivacyPolicy from "../components/layouts/privacy-policy/PrivacyPolicy";
import TestSignPage from "../components/app/erp/payment-request/test-sign/TestSignPage";

import ShipmentForJobPage from "pages/crm/shipment/ShipmentForJobPage";
import ShipmentPlanPage from "pages/crm/shipment/ShipmentPlanPage";
import ShipmentOrderIntakePage from "pages/crm/shipment/ShipmentOrderIntakePage";
import LogisticTrackingPage from "pages/crm/logistic/LogisticTrackingPage";

import NewRequestUserAccountPage from "pages/account-management/user-account/NewRequestUserAccountPage";
import RequestUserAccountDetailPage from "pages/account-management/user-account/RequestUserAccountDetailPage";
import RequestUserAccountConfirmationPage from "pages/account-management/user-account/RequestUserAccountConfirmationPage";
import RequestUserAccountPage from "pages/account-management/user-account/RequestUserAccountPage";

import NewRequestTerminalAccountPage from "pages/account-management/terminal-account/NewRequestTerminalAccountPage";
import RequestTerminalAccountDetailPage from "pages/account-management/terminal-account/RequestTerminalAccountDetailPage";
import RequestTerminalAccountConfirmationPage from "pages/account-management/terminal-account/RequestTerminalAccountConfirmationPage";
import RequestTerminalAccountPage from "pages/account-management/terminal-account/RequestTerminalAccountPage";

import TravelCalendar from "components/app/bookings/travel/TravelCalendar";

import ItemPage from "pages/erp/ItemPage";
import PeriodPage from "pages/erp/PeriodPage";
import InventoryTransactionPage from "pages/erp/InventoryTransactionPage";
import InOutInventoryMaterialPage from "pages/erp/InOutInventoryMaterialPage";
import TournamentPage from "pages/sports/TournamentPage";

import ReleaseForecastSalesPage from "pages/crm/release-forecast/ReleaseForecastSalesPage";
import ReleaseForecastCsdPage from "pages/crm/release-forecast/ReleaseForecastCsdPage";
import ReleaseForecastPlanningPage from "pages/crm/release-forecast/ReleaseForecastPlanningPage";

import ProjectDetailPage from "pages/crm/project/ProjectDetailPage";
import ProjectPage from "pages/crm/project/ProjectPage";

const PortalRoutes = () => {
  return (
    <Routes>
      <Route element={<MainLayout />}>
        <Route path="errors/403" element={<Error403 />} />
        <Route path="errors/404" element={<Error404 />} />
        <Route path="errors/500" element={<Error500 />} />
      </Route>

      <Route element={<PublicLayout />}>
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        {/* Hub-Sign test mode: email "Sign now" lands here (no login; token-authorised). */}
        <Route path="test-sign" element={<TestSignPage />} />
      </Route>

      {/*- ------------- Authentication ---------------------------  */}
      <Route path="authentication/login" element={<LoginStitch />} />
      <Route path="authentication/login-oversea" element={<LoginOverseaStitch />} />

      <Route element={<AuthSimpleLayout />}>
        <Route
          path="authentication/forgot-password"
          element={<SimpleForgetPassword />}
        />
        <Route
          path="authentication/reset-password"
          element={<SimplePasswordReset />}
        />
        <Route
          path="authentication/confirm-mail"
          element={<SimpleConfirmMail />}
        />
        <Route path="authentication/verify-email" element={<VerifyEmail />} />
      </Route>

      {/* //--- MainLayout Starts  */}

      <Route element={<PrivateRoute />}>
        <Route element={<MainLayout />}>
          {/*Home*/}
          <Route path="/" element={<MyWorkSpacePage />} />

          {/* My Calendar & Inbox */}
          <Route path="/my/calendar" element={<Suspense fallback={null}><MyCalendarPage /></Suspense>} />
          <Route path="/my/inbox"    element={<Suspense fallback={null}><MyInboxPage /></Suspense>} />

          {/* ── CRM dashboards — nested under /dashboard/crm (domain); Overview is the landing ── */}
          <Route element={<ProtectedRoute permissionNo={"v513"} />}>
            <Route path="dashboard/crm" element={<CrmOverviewDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v508"} />}>
            <Route path="dashboard/crm/sales" element={<SalesDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v511"} />}>
            <Route path="dashboard/crm/quotation" element={<QuotationDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v512"} />}>
            <Route path="dashboard/crm/job-entry" element={<JobEntryDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v509"} />}>
            <Route path="dashboard/crm/contract" element={<ContractDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v507"} />}>
            <Route path="dashboard/crm/change-order" element={<ChangeOrderDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v510"} />}>
            <Route path="dashboard/crm/waiver" element={<WaiverDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v517"} />}>
            <Route path="dashboard/crm/logistic" element={<Suspense fallback={null}><LogisticDashboardPage /></Suspense>} />
          </Route>

          {/* ── Booking dashboards ── */}
          <Route element={<ProtectedRoute permissionNo={"v514"} />}>
            <Route path="dashboard/booking/car" element={<CarBookingDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v515"} />}>
            <Route path="dashboard/booking/travel" element={<TravelBookingDashboardPage />} />
          </Route>

          {/* ── User sessions dashboard ── */}
          <Route element={<ProtectedRoute permissionNo={"v516"} />}>
            <Route path="dashboard/users" element={<UserSessionsDashboardPage />} />
          </Route>

          {/* Redirects from the legacy flat paths so old bookmarks/links keep working */}
          <Route path="dashboard/sales"        element={<Navigate to="/dashboard/crm/sales" replace />} />
          <Route path="dashboard/quotation"    element={<Navigate to="/dashboard/crm/quotation" replace />} />
          <Route path="dashboard/job-entry"    element={<Navigate to="/dashboard/crm/job-entry" replace />} />
          <Route path="dashboard/contract"     element={<Navigate to="/dashboard/crm/contract" replace />} />
          <Route path="dashboard/change-order" element={<Navigate to="/dashboard/crm/change-order" replace />} />
          <Route path="dashboard/waiver"       element={<Navigate to="/dashboard/crm/waiver" replace />} />

          {/* Payment Request (QF-FNC-01) — feature-flag v960 (:internal, chưa release). */}
          <Route element={<ProtectedRoute permissionNo={"v960"} />}>
            <Route path="payment-requests" element={<PaymentRequestPage />} />
            {/* Form tạo/sửa là route riêng (trước đây là drawer) — deep-link được
                và F5 không mất trang đang mở. */}
            <Route path="payment-requests/new" element={<PaymentRequestForm mode="create" />} />
            <Route path="payment-requests/:id/edit" element={<PaymentRequestForm mode="edit" />} />
            <Route path="payment-requests/:id" element={<PaymentRequestDetail />} />
            <Route path="payment-requests/:id/print" element={<PaymentRequestPrint />} />
          </Route>

          {/* Purchase Requisition (QF-LGS-01 / QF-PUR-01) — feature-flag v961 (:internal). */}
          <Route element={<ProtectedRoute permissionNo={"v961"} />}>
            <Route path="purchase-requests" element={<PurchaseRequisitionsPage />} />
            <Route path="purchase-requests/:id" element={<PurchaseRequestDetail />} />
          </Route>
          {/* Purchase Order (QF-PUR-03) — feature-flag v962 (:internal). */}
          <Route element={<ProtectedRoute permissionNo={"v962"} />}>
            <Route path="purchase-orders" element={<PurchaseOrderPage />} />
            <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
          </Route>

          {/* <Route path="dashboard/saas" element={<Saas />} />
          <Route path="dashboard/e-commerce" element={<Ecommerce />} />
          <Route path="dashboard/lms" element={<Lms />} />
          <Route
            path="dashboard/project-management"
            element={<ProjectManagement />}
          />
          <Route path="dashboard/support-desk" element={<SupportDesk />} /> */}

          {/* Booking */}
          <Route path="booking/meeting-room" element={<MeetingRoomSystem />} />

          {/* HR */}
          <Route path="hr" element={<HrmHubSystem />} />
          <Route path="booking/car/list" element={<CarBookingPage />} />
          <Route
            path="booking/car/new-car-booking"
            element={<CarBookingFormPage mode="new" />}
          />
          <Route path="booking/car/:id/edit" element={<CarBookingFormPage mode="edit" />} />
          <Route path="booking/car/:id" element={<CarBookingDetail />} />
          <Route
            path="booking/car/:id/driver-trans/:driver_tran_id/confirm"
            element={<TransactionDriverConfirm />}
          />
          <Route
            path="booking/car/:id/driver-trans/:driver_tran_id/update"
            element={<TransactionDriverUpdateDeparture />}
          />
          <Route path="booking/car/calendar" element={<CarCalendar />} />
          <Route path="booking/car/settings/cars" element={<Cars />} />
          <Route path="booking/car/settings/drivers" element={<Drivers />} />

          <Route
            path="booking/travel/new-travel-request"
            element={<TravelBookingFormPage mode="new" />}
          />
          <Route path="booking/travel/list" element={<TravelBookingPage />} />
          <Route path="booking/travel/:id" element={<TravelBookingDetailPage />} />
          <Route
            path="booking/travel/:id/edit"
            element={<TravelBookingFormPage mode="edit" />}
          />
          <Route path="booking/travel/:id/print" element={<PrintBooking />} />
          <Route path="booking/travel/calendar" element={<TravelCalendar />} />
          <Route
            path="booking/travel/settings/expense-policy"
            element={<ExpensePolicyPage />}
          />
          <Route
            path="booking/travel/settings/approval-flows"
            element={<ApprovalFlowPage />}
          />

          {/* AI */}
          <Route element={<ProtectedRoute permissionNo={"v1020"} />}>
            <Route path="doc-search" element={<DocSearch />} />
          </Route>
          <Route path="doc-chatbot" element={<DocChatBot />} />

          {/*Drive */}
          <Route path="drives/list" element={<DriveListPage />} />
          <Route path="drives/:id" element={<DriveListPage />} />
          <Route path="drives/:id/viewer" element={<DriveDocViewer />} />
          <Route path="drives/:id/editor" element={<DriveOnlyOfficeEditor />} />
          <Route path="drives/trash/list" element={<DriveTrashPage />} />
        
          {/*Request user account */}
          <Route
            path="request/user-accounts/new"
            element={<NewRequestUserAccountPage />}
          />
          <Route
            path="request/user-accounts/:id"
            element={<RequestUserAccountDetailPage />}
          />
          <Route
            path="request/user-accounts/:id/confirmation"
            element={<RequestUserAccountConfirmationPage />}
          />
          <Route
            path="request/user-accounts/list"
            element={<RequestUserAccountPage />}
          />

          {/*Request terminal account */}
          <Route
            path="request/terminal-accounts/new"
            element={<NewRequestTerminalAccountPage />}
          />
          <Route
            path="request/terminal-accounts/:id"
            element={<RequestTerminalAccountDetailPage />}
          />
          <Route
            path="request/terminal-accounts/:id/confirmation"
            element={<RequestTerminalAccountConfirmationPage />}
          />
          <Route
            path="request/terminal-accounts/list"
            element={<RequestTerminalAccountPage />}
          />


          {/*ERP*/}
          <Route element={<ProtectedRoute permissionNo={"v917"} />}>
            <Route path="erp/items" element={<ItemPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9171"} />}>
            <Route
              path="erp/inventory/transactions"
              element={<InventoryTransactionPage />}
            />
            <Route
              path="erp/inventory/in-out-materials"
              element={<InOutInventoryMaterialPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9172"} />}>
            <Route path="erp/periods" element={<PeriodPage />} />
          </Route>

          {/*CRM*/}

          {/*Template */}
          <Route path="crm/templates/list" element={<TemplatePage />} />
          <Route path="crm/templates/:id" element={<TemplatePage />} />
          <Route
            path="crm/templates/:id/viewer"
            element={<TemplatePage />}
          />
          <Route
            path="crm/templates/:id/editor"
            element={<DriveOnlyOfficeEditor />}
          />

          <Route element={<ProtectedRoute permissionNo={"v600"} />}>
            <Route path="crm/leads/list" element={<Leads />} />
            <Route path="crm/leads/:id" element={<LeadDetail />} />
            <Route path="crm/leads/:id/edit" element={<EditLead />} />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v601"} />}>
            <Route path="crm/leads/new" element={<NewLead />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v701"} />}>
            <Route path="crm/contacts/list" element={<CRMContacts />} />
            <Route path="crm/contacts/new" element={<NewContact />} />
            <Route path="crm/contacts/:id" element={<ContactDetail />} />
            <Route path="crm/contacts/:id/edit" element={<EditContact />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v801"} />}>
            <Route path="crm/customers/list" element={<Customers />} />
            <Route path="crm/customers/new" element={<NewCustomer />} />
            <Route path="crm/customers/:id" element={<CustomerDetail />} />
            <Route path="crm/customers/:id/edit" element={<EditCustomer />} />
            <Route
              path="crm/customers/:customerId/contacts/:id/edit"
              element={<EditContact />}
            />

            <Route
              path="crm/customers/checking"
              element={<CustomerChecking />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v900"} />}>
            <Route path="crm/projects/list" element={<ProjectPage />} />
            <Route path="crm/projects/:id" element={<ProjectDetailPage />} />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/adaptations/:adaptation_id/pifs/new-pif"
              element={<NewPIF />}
            />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/adaptations/:adaptation_id/pifs/:id/edit"
              element={<EditPIF />}
            />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/adaptations/:adaptation_id/pifs/:id"
              element={<PIFDetail />}
            />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/adaptations/:adaptation_id/pifs/:id/print"
              element={<PIFPrint />}
            />

            <Route
              path="crm/projects/:project_id/buildings/:building_id/change-orders/new"
              element={<NewChangeOrder />}
            />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/change-orders/:id/edit"
              element={<EditChangeOrder />}
            />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/change-orders/:id"
              element={<ChangeOrderDetail />}
            />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/change-orders/:id/print"
              element={<ChangeOrderPrint />}
            />
            <Route
              path="crm/projects/:project_id/buildings/:building_id/change-orders/:change_order_id/revisions/:revision_id/pif/edit"
              element={<RevisionPIFEdit />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v903"} />}>
            <Route path="crm/projects/quotations" element={<QuotationPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v904"} />}>
            <Route
              path="crm/projects/master-schedule"
              element={<MasterProjectSchedulePage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v905"} />}>
            <Route
              path="crm/projects/warranties"
              element={<WarrantyPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v908"} />}>
            <Route
              path="crm/projects/schedule-pmd"
              element={<ProjectSchedulePMDPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v909"} />}>
            <Route
              path="crm/projects/interactions"
              element={<InteractionPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v910"} />}>
            <Route path="crm/projects/waivers" element={<Waivers />} />
            <Route
              path="crm/projects/:project_id/waivers/new"
              element={<NewWaiver />}
            />
            <Route
              path="crm/projects/:project_id/waivers/:id/edit"
              element={<EditWaiver />}
            />
            <Route
              path="crm/projects/:project_id/waivers/:id"
              element={<WaiverDetail />}
            />
            <Route
              path="crm/projects/:project_id/waivers/:id/print"
              element={<WaiverPrint />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v911"} />}>
            <Route path="crm/contracts" element={<Contracts />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v919"} />}>
            <Route path="crm/contracts/draft-check" element={<DraftCheckPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9211"} />}>
            <Route path="crm/contracts/pif-check" element={<PifCheckPage />} />
          </Route>
          {/* A Draft Check result: the API checks it is the viewer's own (or they are admin). */}
          <Route path="crm/document-comparisons/:id" element={<ComparisonViewerPage />} />

          <Route element={<ProtectedRoute permissionNo={"v912"} />}>
            <Route path="crm/sales-units/list" element={<SalesUnits />} />
            <Route path="crm/sales-units/:id" element={<SalesUnitDetail />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v918"} />}>
            <Route
              path="crm/projects/price-matrix"
              element={<PriceMatrixPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v906"} />}>
            <Route path="crm/reports/pifs" element={<PIFPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v907"} />}>
            <Route path="crm/reports/iccc" element={<ICCCPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9131"} />}>
            <Route
              path="crm/reports/project-master-summary"
              element={<MasterSummaryPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9132"} />}>
            <Route
              path="crm/reports/project-schedule-order-entry"
              element={<ProjectScheduleOEPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9133"} />}>
            <Route
              path="crm/reports/estimate-assignment-schedule"
              element={<EstimateAssignmentSchedulePage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9134"} />}>
            <Route
              path="crm/reports/engineering-job-progress"
              element={<EngineeringJobProgressPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9135"} />}>
            <Route
              path="crm/reports/project/shop-by-change-order"
              element={<ShopByChangeOrderPage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9136"} />}>
            <Route
              path="crm/reports/project/shop-summary"
              element={<ShopSummaryPage />}
            />
          </Route>

          {/*Shipment */}
          <Route element={<ProtectedRoute permissionNo={"v9152"} />}>
            <Route path="crm/shipments/jobs" element={<ShipmentForJobPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9153"} />}>
            <Route path="crm/shipments/plan" element={<ShipmentPlanPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9154"} />}>
            <Route
              path="crm/shipments/orders"
              element={<ShipmentOrderIntakePage />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9161"} />}>
            <Route
              path="crm/logistics/tracking"
              element={<LogisticTrackingPage />}
            />
          </Route>

          {/* Release Forecast */}
          <Route element={<ProtectedRoute permissionNo={"v1101"} />}>
            <Route
              path="crm/release-forecast/sales"
              element={<ReleaseForecastSalesPage />}
            />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v1102"} />}>
            <Route
              path="crm/release-forecast/csd"
              element={<ReleaseForecastCsdPage />}
            />
          </Route>
          <Route element={<ProtectedRoute permissionNo={"v1112"} />}>
            <Route
              path="crm/release-forecast/planning"
              element={<ReleaseForecastPlanningPage />}
            />
          </Route>

          <Route
            path="organization/employees/:id"
            element={<EmployeeDetail />}
          />

          {/*Organization */}
          <Route element={<ProtectedRoute permissionNo={"v100"} />}>
            <Route path="organization/employees" element={<Employees />} />
            <Route
              path="organization/employees/new"
              element={<NewEmployee />}
            />
            {/* Static segment, so React Router's ranked matching puts it ahead of
                organization/employees/:id regardless of declaration order. */}
            <Route
              path="organization/employees/org-chart"
              element={<EmployeeOrgChart />}
            />
            <Route
              path="organization/employees/:id"
              element={<EmployeeDetail />}
            />
            <Route
              path="organization/employees/:id/edit"
              element={<EditEmployee />}
            />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v101"} />}>
            <Route path="organization/departments" element={<Departments />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v102"} />}>
            <Route path="organization/sites" element={<Sites />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v103"} />}>
            <Route
              path="organization/position-groups"
              element={<PositionGroups />}
            />
          </Route>

          {/*Authorization */}
          <Route element={<ProtectedRoute permissionNo={"v201"} />}>
            <Route path="authorization/users" element={<UserPage />} />
            <Route path="authorization/users/:id" element={<UserDetailPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v202"} />}>
            <Route path="authorization/roles" element={<RolePage />} />
            <Route path="authorization/roles/:id" element={<RoleDetailPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v203"} />}>
            <Route path="authorization/permissions" element={<PermissionPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v205"} />}>
            <Route path="authorization/feature-flags" element={<FeatureFlagsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v518"} />}>
            <Route path="authorization/audit-logs" element={<AuditLogsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v9500"} />}>
            <Route path="document/extractions" element={<DocumentExtractionsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissionNo={"v204"} />}>
            <Route
              path="authorization/report/role-user-permissions"
              element={<RoleUserPermissionPage />}
            />
          </Route>

          <Route path="user/profile" element={<ProfileUser />} />

          {/*Background job */}
          <Route element={<ProtectedRoute permissionNo={"v301"} />}>
            <Route path="sidekiq" element={<BackgroundJobPage />} />
          </Route>

          {/*Integration */}
          <Route element={<ProtectedRoute permissionNo={"v302"} />}>
            <Route
              path="integration/applications/list"
              element={<IntegrationApplication />}
            />
            <Route
              path="integration/applications/new"
              element={<NewApplication />}
            />
            <Route
              path="integration/applications/:id"
              element={<ApplicationDetail />}
            />
            <Route
              path="integration/applications/:id/edit"
              element={<EditApplication />}
            />
          </Route>

          {/*Settings */}
          <Route element={<ProtectedRoute permissionNo={"v303"} />}>
            <Route path="settings" element={<AppSettingsPage />} />
          </Route>

          {/*Notifications list */}
          <Route path="notifications" element={<NotificationList />} />

          {/*Documentation*/}
          <Route path="documentation/faqs" element={<FaqPage />} />
          <Route path="documentation/user-guide"        element={<UserGuidePage />} />
          <Route path="documentation/user-guide/:slug"  element={<UserGuidePage />} />
          <Route path="documentation/changelog" element={<ChangelogPage />} />
          <Route path="documentation/changelog/:version" element={<ChangelogPage />} />

          {/*Sports*/}
          <Route element={<ProtectedRoute permissionNo={"v307"} />}>
            <Route path="sports/tournament" element={<TournamentPage />} />
          </Route>
          
        </Route>
      </Route>

      {/* //--- MainLayout end  */}

      <Route path="*" element={<Navigate to="/errors/404" replace />} />
    </Routes>
  );
};

export default PortalRoutes;
